import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from "../config/config";
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { persistSessionUser } from '../utils/sessionUser';
import { readBookingDraft } from '../utils/bookingDraft';

const OTPVerification = ({ showOTPModal, setShowOTPModal, setShowSigninModal, setShowSignupModal, mobile, otpIntent }) => {
  const { register, handleSubmit, formState: { errors }, setValue } = useForm();
  const [loading, setLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(300);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (showOTPModal) {
      setResendSeconds(300);
      setMessage('');
      [1, 2, 3, 4].forEach((index) => setValue(`otp${index}`, ''));
      const timer = setInterval(() => {
        setResendSeconds((prev) => {
          if (prev <= 0) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showOTPModal, setValue]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleOTPChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 1) {
      setValue(`otp${index}`, value, { shouldValidate: true });
      if (value.length === 1 && index < 4) {
        document.getElementById(`otp${index + 1}`).focus();
      }
    }
  };

  const onSubmit = async (data) => {
    const otp = `${data.otp1}${data.otp2}${data.otp3}${data.otp4}`;
    if (!/^\d{4}$/.test(otp) || !mobile || !otpIntent) {
      setMessage('Please enter a valid OTP or request a new one.');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const deviceKey = 'instaport_customer_device_id';
      let deviceId = localStorage.getItem(deviceKey);
      if (!deviceId) {
        deviceId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        localStorage.setItem(deviceKey, deviceId);
      }
      const response = await axios.post(`${API_URL}/otp/verify-otp`, {
        mobile, otp, accountType: 'customer', purpose: 'customer-auth',
        ...otpIntent,
        deviceInfo: {
          deviceId,
          deviceName: navigator.userAgentData?.platform ? `${navigator.userAgentData.platform} browser` : 'Web browser',
          platform: navigator.userAgentData?.platform || navigator.platform || 'Web',
          browserName: navigator.userAgent || 'Browser',
          appName: 'customerWebsite',
        },
      });
      const result = response.data;
      if (!result.success || !result.token || !result.user) {
        throw new Error(result.message || 'OTP verification failed.');
      }
      localStorage.setItem('token', result.token);
      persistSessionUser(result.user);
      setShowOTPModal(false);
      toast.success(result.message || 'Logged in successfully');
      navigate(readBookingDraft() ? '/create-order' : '/');
    } catch (error) {
      setMessage(error.response?.data?.message || error.message || 'OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setMessage('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/otp/send-otp`, {
        mobile, accountType: 'customer', purpose: 'customer-auth', authMode: otpIntent?.authMode,
      });
      if (!response.data.success) throw new Error(response.data.message || 'Failed to resend OTP');
      setResendSeconds(300);
      toast.success('OTP resent successfully');
    } catch (error) {
      setMessage(error.response?.data?.message || error.message || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  if (!showOTPModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 sm:p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => setShowOTPModal(false)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-2xl font-medium leading-none text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700 sm:h-11 sm:w-11 sm:text-[28px]"
            aria-label="Close verification form"
          >
            ×
          </button>
          <div className="flex items-center justify-center">
            <img src="/assets/logo/logo.png" alt="Instaport Logo" className="h-16 sm:h-20 w-auto" />
          </div>
          <div className="h-10 w-10 sm:h-11 sm:w-11" aria-hidden="true" />
        </div>
        <h2 className="mb-6 text-3xl font-bold">Verify Your Number</h2>
        <p className="mb-8 text-gray-600">We've sent a 4-digit code to +91 {mobile}</p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex justify-between mb-8">
            {[1, 2, 3, 4].map((index) => (
              <input
                key={index}
                id={`otp${index}`}
                type="text"
                maxLength={1}
                {...register(`otp${index}`, {
                  required: 'OTP digit required',
                  pattern: { value: /^\d$/, message: 'Must be a digit' },
                })}
                onChange={(e) => handleOTPChange(e, index)}
                className="w-14 h-14 text-center text-lg border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            ))}
          </div>
          {errors.otp1 && <p className="text-red-500 text-sm mb-4">{errors.otp1.message}</p>}
          {message && <p className="text-red-500 text-sm mb-4 text-center">{message}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-yellow-500 text-white p-3 rounded-md hover:bg-yellow-600 transition-colors disabled:bg-yellow-300"
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
        <div className="mt-4 text-center">
          <button
            onClick={resendSeconds === 0 && !loading ? handleResend : undefined}
            disabled={resendSeconds !== 0 || loading}
            className={`text-yellow-500 hover:underline ${resendSeconds !== 0 ? 'text-gray-400 cursor-not-allowed' : ''}`}
          >
            {resendSeconds === 0 ? 'Resend code' : `Resend in ${formatTime(resendSeconds)}`}
          </button>
        </div>
        <div className="mt-4 text-center">
          <button
            onClick={() => {
              setShowOTPModal(false);
              if (otpIntent?.authMode === 'signup' && setShowSignupModal) setShowSignupModal(true);
              else               setShowSigninModal(true);
            }}
            className="text-yellow-500 hover:underline"
          >
            Use different number
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
